import { Draft, Patch, createDraft } from 'immer';
import { PartialSheet, Sheet, SheetView } from '../../types';
import { SheetManagementSet } from '../../types/sheet/draft';
import { acquireRecord } from '../../../core/utils/lock/lockMechanism';
import { getStaticSheet } from '../commands/execute/sheetCRUD';

interface Updates {
  updatedSheet?: PartialSheet;
  changes?: Patch[];
  updatedSheetView?: Partial<SheetView>;
}

export const saveSheetUpdatesInSet = (
  sheetId: string,
  set: SheetManagementSet,
  { updatedSheet, updatedSheetView, changes }: Updates
) => {
  if (updatedSheet) {
    const existingSheet: PartialSheet = set.updatedSheets.get(sheetId) || {
      id: sheetId,
    };
    set.updatedSheets.set(sheetId, {
      ...existingSheet,
      ...updatedSheet,
    });
  }

  if (changes) {
    if (!set.present.sheetChanges) set.present.sheetChanges = new Map();
    const existingChanges = set.present.sheetChanges.get(sheetId) || [];
    set.present.sheetChanges.set(sheetId, existingChanges.concat(changes));
  }

  if (updatedSheetView) {
    const existingSheetView = set.updatedSheetViews.get(sheetId) || {};
    set.updatedSheetViews.set(sheetId, {
      ...existingSheetView,
      ...updatedSheetView,
    });
  }
};

export const createSheetDraft = async (
  sheetId: string,
  set: SheetManagementSet,
  stopDraftCreation = false
): Promise<Draft<Sheet>> => {
  if (!set.staticEntityIdToNameMap.sheetsIdToName[sheetId])
    throw new Error(`sheet ${sheetId} not found`);

  if (!set.temporarySheets.has(sheetId) && !set.isTemporarySet) {
    await acquireRecord(sheetId, 'd');
  }

  const sheet = await getStaticSheet(set, sheetId);

  if (!sheet) throw new Error(`sheet ${sheetId} not found`);

  if (!set.temporarySheets.has(sheetId)) {
    set.temporarySheets.set(sheetId, sheet);
  }

  let sheetDraft = set.draftedSheets.get(sheetId);

  if (!set.draftedSheets.has(sheetId) && !stopDraftCreation) {
    sheetDraft = createDraft(sheet);
    set.draftedSheets.set(sheetId, sheetDraft);
  }

  return sheetDraft!;
};
